import { Injectable } from '@angular/core';
import { SupabaseClient, createClient } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment.development';
import type { Database } from '../db/database.types';

/**
 * Único punto donde se crea el cliente de Supabase en toda la aplicación.
 * Crear un segundo cliente duplica el listener de sesión y provoca refrescos
 * de token en competencia.
 *
 * El cliente se construye de forma perezosa a propósito. En la build de
 * producción, `environment.ts` contiene los marcadores `#{SUPABASE_URL}#` que
 * el pipeline sustituye más tarde; si el cliente se creara en el constructor,
 * la extracción de rutas de Angular fallaría con "Invalid supabaseUrl" antes de
 * que esa sustitución ocurra.
 */
@Injectable({ providedIn: 'root' })
export class SupabaseService {
  private instance: SupabaseClient<Database> | null = null;

  /**
   * Falso cuando la URL sigue siendo un marcador sin sustituir. Los consumidores
   * deben comprobarlo antes de pedir datos, en vez de reventar.
   */
  readonly isConfigured = /^https?:\/\//.test(environment.supabaseUrl);

  get client(): SupabaseClient<Database> {
    if (!this.instance) {
      this.instance = createClient<Database>(environment.supabaseUrl, environment.supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      });
    }
    return this.instance;
  }

  get auth(): SupabaseClient<Database>['auth'] {
    return this.client.auth;
  }
}
